import { Group, Stack } from "@mantine/core"
import { SectionHeader } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { SubscriptionPlan } from "@tfg/types/endpoints/subscriptionPlan"

import { queryClient } from "~/backend/queryClient"
import { useSaveSubscriptionPlan } from "~/gen/api"
import { SubscriptionPlanCard } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/components/SubscriptionPlanCard"
import { SUBSCRIPTION_PLANS_WITH_TRANSLATIONS } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/constants/subscriptionPlansWithTranslations.constant"
import { useOrganizationSettings } from "~/pages/Brand/CommunicationStudio/contexts/organizationSettings/useOrganizationSettings"
import { useOrganizationSubscriptionPlanPermissions } from "~/pages/Brand/CommunicationStudio/contexts/organizationSubscriptionPlanPermissions/useOrganizationSubscriptionPlanPermissions"

export const SubscriptionPlans = () => {
  const { t } = useTranslation("communicationStudio")
  const organizationSettings = useOrganizationSettings()
  const organizationId = organizationSettings.organization.id
  const organizationSlug = organizationSettings.organization.slug

  const { setSubscriptionPlan: setSubscriptionPlanForPermissions } =
    useOrganizationSubscriptionPlanPermissions()

  const currentSubscriptionPlan =
    organizationSettings.organization.settings.subscriptionPlan

  const { mutate: saveSubscriptionPlan } = useSaveSubscriptionPlan({
    mutation: {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["allOrganizationSettings", organizationId],
        })
        setSubscriptionPlanForPermissions(
          variables.data.subscriptionPlan as SubscriptionPlan
        )
      },
    },
  })

  const handleSubscriptionPlanChange = (subscriptionPlan: SubscriptionPlan) => {
    saveSubscriptionPlan({
      organizationId,
      data: { subscriptionPlan, slug: organizationSlug },
    })
  }

  return (
    <Stack w="fit-content">
      <SectionHeader
        title={t("tabs.general.subscriptionPlans.title")}
        subtitle={t("tabs.general.subscriptionPlans.subtitle")}
        w="100%"
        maw="90rem"
      />
      <Group w="100%" justify="flex-start" gap="md" h="fit-content">
        {(
          Object.entries(SUBSCRIPTION_PLANS_WITH_TRANSLATIONS) as [
            Exclude<SubscriptionPlan, "NONE">,
            { name: string; description: string },
          ][]
        ).map(([key, { name, description }]) => (
          <SubscriptionPlanCard
            key={key}
            subscriptionPlan={key}
            name={name}
            description={description}
            checked={key === currentSubscriptionPlan}
            onClick={handleSubscriptionPlanChange}
          />
        ))}
      </Group>
    </Stack>
  )
}
